import React from 'react';

/**
 * Shared dark-space sky used by Login / Forgot password and similar auth screens.
 *
 * Visual concept: a still night/day sky — moon (night) on the left, sun (day) on
 * the right — representing "ShahoSuite running day and night". Stars are fixed in
 * place and only twinkle (opacity/scale pulse); nothing pans or drifts. A shooting
 * star crosses the sky on a steady ~22s cadence, alternating direction.
 */
const AuthSkyBackground: React.FC = () => {
  // Distant dust: tiny, low-contrast, fixed points. Subtle twinkle only.
  const farStars = [
    { w: 1, top: '6%', left: '12%', dur: 3.4, del: 0, base: 0.18, peak: 0.42 },
    { w: 1, top: '14%', left: '28%', dur: 4.1, del: 0.6, base: 0.16, peak: 0.4 },
    { w: 1, top: '9%', left: '46%', dur: 3.7, del: 1.2, base: 0.2, peak: 0.46 },
    { w: 1, top: '22%', left: '61%', dur: 4.6, del: 0.3, base: 0.15, peak: 0.38 },
    { w: 1, top: '18%', left: '79%', dur: 3.9, del: 1.8, base: 0.18, peak: 0.44 },
    { w: 1, top: '35%', left: '8%', dur: 4.3, del: 0.9, base: 0.17, peak: 0.4 },
    { w: 1, top: '41%', left: '33%', dur: 5.1, del: 1.5, base: 0.16, peak: 0.36 },
    { w: 1, top: '38%', left: '72%', dur: 3.6, del: 0.2, base: 0.2, peak: 0.48 },
    { w: 1, top: '52%', left: '19%', dur: 4.4, del: 2.1, base: 0.18, peak: 0.42 },
    { w: 1, top: '58%', left: '55%', dur: 5.3, del: 0.5, base: 0.15, peak: 0.38 },
    { w: 1, top: '64%', left: '88%', dur: 3.8, del: 1.3, base: 0.19, peak: 0.44 },
    { w: 1, top: '71%', left: '41%', dur: 4.7, del: 1.9, base: 0.17, peak: 0.4 },
    { w: 1, top: '78%', left: '14%', dur: 4.0, del: 0.7, base: 0.18, peak: 0.42 },
    { w: 1, top: '84%', left: '67%', dur: 4.9, del: 2.4, base: 0.16, peak: 0.38 },
    { w: 1, top: '91%', left: '29%', dur: 3.5, del: 1.1, base: 0.2, peak: 0.46 },
    { w: 1, top: '4%', left: '88%', dur: 4.2, del: 0.4, base: 0.15, peak: 0.36 },
    { w: 1, top: '47%', left: '48%', dur: 5.0, del: 1.6, base: 0.18, peak: 0.42 },
    { w: 1, top: '26%', left: '94%', dur: 3.9, del: 2.2, base: 0.19, peak: 0.44 },
  ];

  // Near stars: fixed points, larger + brighter to suggest depth/closeness, with a
  // more pronounced twinkle (and a soft glow that fades in sync with opacity).
  // Position and size never change — only brightness/scale pulse.
  const nearStars = [
    { size: 2.2, top: '10%', left: '38%', dur: 3.2, del: 0.4, base: 0.35, peak: 1, color: '255,255,255' },
    { size: 1.6, top: '15%', left: '58%', dur: 4.1, del: 1.2, base: 0.4, peak: 0.9, color: '255,244,224' },
    { size: 2.4, top: '25%', left: '86%', dur: 3.6, del: 2.0, base: 0.3, peak: 1, color: '214,225,255' },
    { size: 1.8, top: '31%', left: '5%', dur: 4.6, del: 0.8, base: 0.4, peak: 0.95, color: '255,255,255' },
    { size: 2.2, top: '44%', left: '22%', dur: 3.9, del: 1.6, base: 0.35, peak: 1, color: '255,244,224' },
    { size: 1.5, top: '40%', left: '63%', dur: 4.4, del: 2.4, base: 0.4, peak: 0.85, color: '214,225,255' },
    { size: 2.0, top: '60%', left: '78%', dur: 3.4, del: 0.6, base: 0.3, peak: 1, color: '255,255,255' },
    { size: 1.7, top: '68%', left: '12%', dur: 4.8, del: 1.8, base: 0.38, peak: 0.9, color: '255,244,224' },
    { size: 2.3, top: '75%', left: '48%', dur: 3.7, del: 2.6, base: 0.32, peak: 1, color: '214,225,255' },
    { size: 1.6, top: '82%', left: '91%', dur: 4.2, del: 0.2, base: 0.4, peak: 0.88, color: '255,255,255' },
    { size: 2.0, top: '55%', left: '35%', dur: 3.9, del: 1.0, base: 0.35, peak: 0.95, color: '255,244,224' },
    { size: 1.8, top: '20%', left: '16%', dur: 4.5, del: 2.2, base: 0.38, peak: 0.9, color: '214,225,255' },
  ];

  return (
    <>
      <style>{`
        @keyframes authSkyBreath {
          0%,100% { opacity: .6; }
          50% { opacity: 1; }
        }
        /* Fixed-position twinkle: opacity + scale pulse only — never translates. */
        @keyframes authTwinkle {
          0%,100% { opacity: var(--base, .3); transform: scale(.82); }
          50% { opacity: var(--peak, .9); transform: scale(1); }
        }
        @keyframes authFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
        @keyframes authCoronaPulse { 0%,100%{opacity:.18;transform:scale(1)} 50%{opacity:.32;transform:scale(1.06)} }
        @keyframes authCoronaPulse2 { 0%,100%{opacity:.1;transform:scale(1)} 50%{opacity:.2;transform:scale(1.1)} }
        @keyframes authSunGlow {
          0%,100%{box-shadow:0 0 40px 16px rgba(255,190,40,.2),0 0 100px 42px rgba(255,140,0,.09)}
          50%{box-shadow:0 0 58px 24px rgba(255,200,60,.3),0 0 130px 58px rgba(255,150,0,.14)}
        }
        @keyframes authMoonGlow {
          0%,100%{box-shadow:0 0 18px 4px rgba(180,190,255,.18),0 0 40px 12px rgba(120,140,220,.08)}
          50%{box-shadow:0 0 26px 7px rgba(200,210,255,.26),0 0 54px 16px rgba(140,160,240,.12)}
        }
        /* Shooting stars: two directions, each looping every 44s and offset by
           22s from one another, so together a new meteor streaks across the sky
           on a steady ~22s cadence, alternating left-to-right / right-to-left. */
        @keyframes authShootLTR {
          0% { transform: translate3d(0,0,0) rotate(16deg) scale(1); opacity: 0; }
          3% { opacity: 1; }
          15% { opacity: 1; transform: translate3d(78vw, 32vh, 0) rotate(16deg) scale(1.05); }
          20% { transform: translate3d(96vw, 40vh, 0) rotate(16deg) scale(.9); opacity: 0; }
          100% { transform: translate3d(96vw, 40vh, 0) rotate(16deg) scale(.9); opacity: 0; }
        }
        @keyframes authShootRTL {
          0% { transform: translate3d(0,0,0) rotate(-16deg) scale(1); opacity: 0; }
          3% { opacity: 1; }
          15% { opacity: 1; transform: translate3d(-78vw, 32vh, 0) rotate(-16deg) scale(1.05); }
          20% { transform: translate3d(-96vw, 40vh, 0) rotate(-16deg) scale(.9); opacity: 0; }
          100% { transform: translate3d(-96vw, 40vh, 0) rotate(-16deg) scale(.9); opacity: 0; }
        }
        .auth-shoot {
          position: absolute;
          width: 240px;
          height: 2px;
          border-radius: 3px;
          pointer-events: none;
          z-index: 2;
          animation-timing-function: cubic-bezier(.22,.61,.36,1);
        }
        .auth-shoot::after {
          content: '';
          position: absolute;
          width: 6px;
          height: 6px;
          top: -2px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 0 14px 4px rgba(255,255,255,.9);
        }
        .auth-shoot-ltr {
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.12) 25%, rgba(255,250,240,.85) 70%, #fff 100%);
        }
        .auth-shoot-ltr::after { right: -2px; }
        .auth-shoot-rtl {
          background: linear-gradient(270deg, transparent 0%, rgba(255,255,255,.12) 25%, rgba(255,250,240,.85) 70%, #fff 100%);
        }
        .auth-shoot-rtl::after { left: -2px; }
        @media (prefers-reduced-motion: reduce) {
          .auth-sky, .auth-star, .auth-shoot, .auth-float, .auth-sun {
            animation: none !important;
          }
        }
      `}</style>

      {/* Sky: moon/night on the left, sun/day on the right — "always running, day
          and night". A slow overall breathing pulse keeps it feeling alive without
          any panning or drifting. */}
      <div
        className="auth-sky"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 440px 320px at 92% 3%, rgba(130,64,8,.6) 0%, transparent 74%),' +
            'radial-gradient(ellipse 260px 200px at 84% 2%, rgba(255,170,60,.16) 0%, transparent 70%),' +
            'radial-gradient(ellipse 600px 460px at 5% 12%, rgba(28,40,120,.5) 0%, transparent 70%),' +
            'radial-gradient(ellipse 720px 440px at 50% 0%, rgba(8,12,40,.95) 0%, transparent 78%),' +
            '#03040e',
          animation: 'authSkyBreath 24s ease-in-out infinite',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 220,
          background: 'radial-gradient(ellipse 90% 100% at 50% 100%, rgba(14,20,58,.8) 0%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      {farStars.map((s, i) => (
        <div
          key={`f${i}`}
          className="auth-star"
          style={{
            position: 'absolute',
            width: s.w,
            height: s.w,
            borderRadius: '50%',
            background: 'rgba(220,215,245,.9)',
            top: s.top,
            left: s.left,
            ['--base' as string]: s.base,
            ['--peak' as string]: s.peak,
            animation: `authTwinkle ${s.dur}s ease-in-out ${s.del}s infinite`,
          } as React.CSSProperties}
        />
      ))}
      {nearStars.map((s, i) => (
        <div
          key={`n${i}`}
          className="auth-star"
          style={{
            position: 'absolute',
            width: s.size,
            height: s.size,
            borderRadius: '50%',
            background: `rgba(${s.color},1)`,
            boxShadow: `0 0 ${s.size * 4}px ${s.size * 1.2}px rgba(${s.color},.55)`,
            top: s.top,
            left: s.left,
            ['--base' as string]: s.base,
            ['--peak' as string]: s.peak,
            animation: `authTwinkle ${s.dur}s ease-in-out ${s.del}s infinite`,
          } as React.CSSProperties}
        />
      ))}

      {/* Shooting stars — steady ~22s cadence, alternating direction (see keyframe
          comment above for how the two 44s-period streams line up). */}
      <div
        className="auth-shoot auth-shoot-ltr"
        style={{ top: '8%', left: '-260px', animation: 'authShootLTR 44s linear 3s infinite' }}
      />
      <div
        className="auth-shoot auth-shoot-rtl"
        style={{ top: '17%', right: '-260px', animation: 'authShootRTL 44s linear 25s infinite' }}
      />

      {/* Realistic soft sun — corona glow, no hard ray spokes */}
      <div
        className="auth-sun"
        style={{
          position: 'absolute',
          top: 12,
          right: 28,
          width: 140,
          height: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,200,60,.2) 0%, rgba(255,140,0,.06) 48%, transparent 74%)',
            animation: 'authCoronaPulse2 5.5s ease-in-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: 112,
            height: 112,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,220,100,.28) 0%, rgba(255,160,30,.12) 52%, transparent 78%)',
            animation: 'authCoronaPulse 3.8s ease-in-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: 72,
            height: 72,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 34% 30%, #fff9e0 0%, #ffe066 18%, #ffc200 42%, #e8820a 70%, #b84a00 100%)',
            animation: 'authSunGlow 4.2s ease-in-out infinite',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 68% 70%, rgba(140,40,0,.4) 0%, transparent 58%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '18%',
              left: '22%',
              width: '28%',
              height: '18%',
              borderRadius: '50%',
              background: 'rgba(255,255,255,.35)',
              filter: 'blur(3px)',
            }}
          />
        </div>
      </div>

      {/* More realistic moon — crater shading + cool glow */}
      <div className="auth-float" style={{ position: 'absolute', top: 18, left: 28, animation: 'authFloat 10s ease-in-out infinite' }}>
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 32% 28%, #f8f6ff 0%, #d8d2f0 38%, #9a92c8 78%, #6a6298 100%)',
            boxShadow: '0 0 22px 5px rgba(160,150,220,.22), inset -8px -10px 16px rgba(30,25,70,.28)',
            overflow: 'hidden',
            position: 'relative',
            animation: 'authMoonGlow 6s ease-in-out infinite',
          }}
        >
          <div style={{ position: 'absolute', top: -8, left: 18, width: 50, height: 50, borderRadius: '50%', background: '#050810', opacity: 0.42 }} />
          <div
            style={{
              position: 'absolute',
              width: 12,
              height: 12,
              top: 15,
              left: 10,
              borderRadius: '50%',
              background: 'rgba(70,60,110,.45)',
              boxShadow: 'inset 1px 1px 2px rgba(0,0,0,.25)',
            }}
          />
          <div style={{ position: 'absolute', width: 7, height: 7, top: 30, left: 26, borderRadius: '50%', background: 'rgba(55,45,95,.4)' }} />
          <div style={{ position: 'absolute', width: 9, height: 9, top: 12, left: 30, borderRadius: '50%', background: 'rgba(75,65,115,.32)' }} />
          <div style={{ position: 'absolute', width: 5, height: 5, top: 36, left: 14, borderRadius: '50%', background: 'rgba(60,50,100,.35)' }} />
        </div>
      </div>
    </>
  );
};

export default AuthSkyBackground;
